import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
} from "react-bootstrap";
import axiosInstance from '../../services/AxiosInstance';
import './commonstyle.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import '../tasks/tasklist.css';
import { isBranchAdmin, isDepartmentAdmin, isSubDepartmentAdmin, getCurrentUserRole } from '../../utils/roleAccess';

const SubDepartmentList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subDepartments, setSubDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubDepartment, setSelectedSubDepartment] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  useEffect(() => {
    fetchSubDepartments();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSubDepartments(subDepartments);
    } else {
      const filtered = subDepartments.filter(sd =>
        (sd.name && sd.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (sd.department_name && sd.department_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (sd.branch_name && sd.branch_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredSubDepartments(filtered);
    }
  }, [subDepartments, searchTerm]);

  useEffect(() => {
    const querySearch = searchParams.get('search') || '';
    if (querySearch !== searchTerm) {
      setSearchTerm(querySearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      const currentUserRole = getCurrentUserRole();
      
      // Build URL with role-based filtering
      let url = `/settings/subDepartments?corporate_id=${corporateId}`;
      
      // If user has restricted access, filter by their branch and/or department
      if (isBranchAdmin(currentUserRole) || isDepartmentAdmin(currentUserRole) || isSubDepartmentAdmin(currentUserRole)) {
        try {
          const userId = userData?.id;
          if (userId) {
            const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
            const userDetails = userDetailsRes.data;
            const branchId = userDetails?.Branch?.id;
            const departmentId = userDetails?.Department?.id;
            const subDepartmentId = userDetails?.SubDepartment?.id;
            
            // Add branch_id parameter if user has a branch
            if (branchId) {
              url += `&branch_id=${branchId}`;
            }
            
            // Add department_id parameter if user has a department
            // (for department_admin and sub_department_admin)
            if (departmentId && (isDepartmentAdmin(currentUserRole) || isSubDepartmentAdmin(currentUserRole))) {
              url += `&department_id=${departmentId}`;
            }
            
            // Note: For sub_department_admin, we could also filter by sub_department_id
            // but since they should see all sub-departments in their department, we just filter by department
          }
        } catch (err) {
          console.error('Error fetching current user details for filtering:', err);
        }
      }
      
      // Try camel-case endpoint first, then fallback to lowercase
      let res;
      try {
        res = await axiosInstance.get(url);
      } catch (e1) {
        // Fallback to lowercase endpoint
        url = url.replace('/settings/subDepartments', '/settings/subdepartments');
        res = await axiosInstance.get(url);
      }
      
      const data = res.data;
      if (res) {
        setSubDepartments(data.data || data || []);
      }
    } catch (error) {
      console.error('Sub-department list fetch error:', error);
      notifyError('Failed to fetch sub-departments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSubDepartment) return;
    const userData = JSON.parse(localStorage.getItem('user'));
    setDeleteLoading(true);
    try {
      const res = await axiosInstance.put(`/settings/deactivateSubDepartment`, { sub_department_id: selectedSubDepartment.id, updated_by: userData.id });
      if (res) {
        notifyTopRight('Sub-department deleted successfully');
        fetchSubDepartments();
        setShowDeleteModal(false);
        setSelectedSubDepartment(null);
      }
    } catch (error) {
      console.error('Delete sub-department error:', error);
      if (error.response?.status === 409) {
        notifyError('Cannot delete sub-department with dependencies');
      } else {
        notifyError(error.response?.data?.error || 'Failed to delete sub-department');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // Define columns for CommonTable
  const columns = [
    {
      key: 'index',
      header: '#',
      accessor: (row, index) => index + 1,
    },
    {
      key: 'name',
      header: 'Sub-Department Name',
      accessor: 'name',
    },
    {
      key: 'department',
      header: 'Department',
      accessor: (row) => row.department_name || 'N/A',
    },
    {
      key: 'branch',
      header: 'Branch',
      accessor: (row) => row.branch_name || 'N/A',
    },
  ];

  // Define actions for CommonTable
  const getActions = (row, rowIndex) => {
    return [
      {
        label: 'Edit',
        icon: 'fas fa-edit',
        onClick: () => navigate(`/settings/editSubDepartment/${row.id}`, { state: { subDepartment: row } }),
      },
      {
        label: 'Delete',
        icon: 'fas fa-trash',
        className: 'text-danger',
        onClick: () => {
          setSelectedSubDepartment(row);
          setShowDeleteModal(true);
        },
      },
    ];
  };

  usePageToolbar(null, []);

  return (
    <Row style={{marginBottom: "0px !important"}}>
      <ToastContainer />
      <Col lg={12}>
        <Card className="user-list-card">
          <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1.5rem' }}>
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <div className="row">
                <CommonTable
                  columns={columns}
                  data={filteredSubDepartments}
                  actions={getActions}
                  emptyMessage={subDepartments.length === 0 ? 'No sub-departments found' : 'No sub-departments match your search criteria'}
                  emptyStateIcon="fas fa-layer-group"
                  getRowId={(row, index) => row.id || index}
                  maxHeight="70vh"
                  minHeight="400px"
                />
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the sub-department "{selectedSubDepartment?.name}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Deleting...</>) : ('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      <BackToTop />
      <InternalFooter />
    </Row>
  );
};

export default SubDepartmentList;


